import React, { useMemo } from 'react';
import _ from 'lodash';
import { BUTTON_TYPES, components as Core, useTranslation, valueBuilder } from '../../../../../core';
import { css, colors, fonts, spacing, StyleSheet } from '../../../../../../styles';
import { CompanyDetailsFooter } from '../CompanyDetailsFooter';
import { CollapsedFieldSection, CompanyFieldGrid, DetailsSection } from '../CompanyDetailsFields';
import { FIELDS, FIELD_TYPES, SHOPIFY_BILLING_INFO_FIELDS } from '../../utils/constants';
import { getSubscriptionPlanName, getSubscriptionProvider, getSubscriptionStatus } from '../../utils/companyDetailsUtils';

/**
 * Renders subscription details, limits, billing controls, and subscription actions.
 *
 * @param {object} props
 * @param {object} props.company
 * @param {object} props.formData
 * @param {Function} props.getFieldValue
 * @param {boolean} props.hasChanges
 * @param {boolean} props.isRemovingSubscription
 * @param {boolean} props.isUpdatingCompany
 * @param {Function} props.onChangeProperty
 * @param {Function} props.onClose
 * @param {Function} props.onOpenManualSubscriptionModal
 * @param {Function} props.onRemoveSubscription
 * @param {Function} props.onSave
 * @param {Array<object>} props.subscriptionPlans
 */
export const SubscriptionTab = ({
    company,
    formData,
    getFieldValue,
    hasChanges,
    isRemovingSubscription = false,
    isUpdatingCompany,
    onChangeProperty,
    onClose,
    onOpenManualSubscriptionModal,
    onRemoveSubscription,
    onSave,
    subscriptionPlans = []
}) => {
    const { t } = useTranslation();

    const subscriptionPlanOptions = useMemo(() => subscriptionPlans.map(subscriptionPlan => ({
        text: `${subscriptionPlan.name}  |  ${subscriptionPlan.price}`,
        value: subscriptionPlan.id
    })), [subscriptionPlans]);

    const providerSubscriptionFields = useMemo(() => {
        if (_.get(company, 'shopifyBillingInfo', false)) {
            return [
                {
                    fieldName: SHOPIFY_BILLING_INFO_FIELDS.shopifyBillingShopUrl,
                    label: t('companies.shopifyBillingInfo.shopUrl'),
                    sourceField: 'shopifyBillingInfo',
                    isReadOnly: true
                },
                {
                    fieldName: SHOPIFY_BILLING_INFO_FIELDS.status,
                    label: t('companies.shopifyBillingInfo.status'),
                    sourceField: 'shopifyBillingInfo',
                    isReadOnly: true
                },
                {
                    fieldName: SHOPIFY_BILLING_INFO_FIELDS.activationDate,
                    label: t('companies.shopifyBillingInfo.activationDate'),
                    sourceField: 'shopifyBillingInfo',
                    type: FIELD_TYPES.date,
                    isReadOnly: true
                },
                {
                    fieldName: SHOPIFY_BILLING_INFO_FIELDS.createdDate,
                    label: t('companies.shopifyBillingInfo.createdDate'),
                    sourceField: 'shopifyBillingInfo',
                    type: FIELD_TYPES.date,
                    isReadOnly: true
                },
                {
                    fieldName: SHOPIFY_BILLING_INFO_FIELDS.subscriptionId,
                    label: t('companies.shopifyBillingInfo.subscriptionId'),
                    sourceField: 'shopifyBillingInfo',
                    isReadOnly: true
                },
                {
                    fieldName: SHOPIFY_BILLING_INFO_FIELDS.tempReplycoSubscriptionId,
                    label: t('companies.shopifyBillingInfo.tempReplycoSubscriptionId'),
                    sourceField: 'shopifyBillingInfo',
                    isReadOnly: true
                }
            ];
        }

        return [
            {
                fieldName: FIELDS.stripeSubscriptionId,
                label: t('companies.stripeSubscriptionId'),
                onChange: onChangeProperty(FIELDS.stripeSubscriptionId)
            },
            {
                fieldName: FIELDS.subscriptionDate,
                label: t('companies.subscriptionDate'),
                type: FIELD_TYPES.date,
                showTime: true,
                onChange: onChangeProperty(FIELDS.subscriptionDate)
            },
            {
                fieldName: FIELDS.subscriptionPlan,
                label: t('companies.subscriptionPlan'),
                type: FIELD_TYPES.select,
                options: subscriptionPlanOptions,
                placeholder: t('companies.subscriptionPlanSelectPlaceholder'),
                onChange: onChangeProperty(FIELDS.subscriptionPlan)
            },
            {
                fieldName: FIELDS.paymentCustomerId,
                label: t('companies.paymentCustomerId'),
                onChange: onChangeProperty(FIELDS.paymentCustomerId)
            },
            {
                fieldName: FIELDS.isManualSubscribed,
                label: t('companies.isManualSubscribed'),
                type: FIELD_TYPES.switch,
                onChange: onChangeProperty(FIELDS.isManualSubscribed)
            }
        ];
    }, [company, onChangeProperty, subscriptionPlanOptions, t]);

    const datesAndLimitsFields = useMemo(() => [
        {
            fieldName: FIELDS.trialExpire,
            label: t('companies.trialExpire'),
            type: FIELD_TYPES.date,
            onChange: onChangeProperty(FIELDS.trialExpire)
        },
        {
            fieldName: FIELDS.notifiedAboutCompanyRemovingDate,
            label: t('companies.notifiedAboutCompanyRemovingDate'),
            type: FIELD_TYPES.date,
            onChange: onChangeProperty(FIELDS.notifiedAboutCompanyRemovingDate)
        },
        {
            fieldName: FIELDS.subscriptionExpiredDate,
            label: t('companies.subscriptionExpiredDate'),
            type: FIELD_TYPES.date,
            onChange: onChangeProperty(FIELDS.subscriptionExpiredDate)
        },
        {
            fieldName: FIELDS.integrationsCount,
            label: t('companies.integrationsCount'),
            type: FIELD_TYPES.number,
            min: 0,
            onChange: onChangeProperty(FIELDS.integrationsCount)
        },
        {
            fieldName: FIELDS.endlessIntegrations,
            label: t('companies.endlessIntegrations'),
            type: FIELD_TYPES.switch,
            onChange: onChangeProperty(FIELDS.endlessIntegrations)
        }
    ], [onChangeProperty, t]);

    const billingConfigurationFields = useMemo(() => [
        {
            fieldName: FIELDS.billingUsage,
            label: t('companies.billingUsage'),
            type: FIELD_TYPES.switch,
            onChange: onChangeProperty(FIELDS.billingUsage)
        },
        {
            fieldName: FIELDS.isOldSubscription,
            label: t('companies.isOldSubscription'),
            type: FIELD_TYPES.switch,
            onChange: onChangeProperty(FIELDS.isOldSubscription)
        },
        {
            fieldName: FIELDS.failedSubscription,
            label: t('companies.failedSubscription'),
            type: FIELD_TYPES.switch,
            onChange: onChangeProperty(FIELDS.failedSubscription)
        },
        {
            fieldName: FIELDS.showUsedTicketsCount,
            label: t('companies.showUsedTicketsCount'),
            type: FIELD_TYPES.switch,
            onChange: onChangeProperty(FIELDS.showUsedTicketsCount)
        }
    ], [onChangeProperty, t]);

    const providerDetailsTitle = _.get(company, 'shopifyBillingInfo') ?
        t('companies.shopifyBillingInfoCollapse') : t('companies.stripeSubscriptionDetails');
    const provider = t(`companies.details.providers.${getSubscriptionProvider(formData)}`);
    const trialExpire = formData.trialExpire;

    return (
        <div>
            <div className={css(styles.subscriptionSummary)}>
                <SummaryItem
                    label={t('companies.details.summary.typeProvider')}
                    value={`${getSubscriptionPlanName(formData, subscriptionPlans, t)} · ${provider}`}
                />
                <SummaryItem
                    label={t('companies.details.summary.currentStatus')}
                    value={getSubscriptionStatus(formData, t)}
                />
                <SummaryItem
                    label={t('companies.details.summary.trialExpiration')}
                    value={valueBuilder.getLocalFullTime(trialExpire) || '—'}
                />
            </div>
            <div className={css(styles.subscriptionActions)}>
                <Core.Button
                    label={t('companies.details.manageManualSubscription')}
                    onPress={onOpenManualSubscriptionModal}
                    type={BUTTON_TYPES.blueOutline}
                />
                <Core.PopConfirm onConfirm={onRemoveSubscription}>
                    <span>
                        <Core.Button
                            isDisabled={isRemovingSubscription}
                            isLoading={isRemovingSubscription}
                            label={t('companies.details.revokeSubscription')}
                            type={BUTTON_TYPES.redOutline}
                        />
                    </span>
                </Core.PopConfirm>
            </div>
            <DetailsSection>
                <CollapsedFieldSection
                    title={providerDetailsTitle}
                    fields={providerSubscriptionFields}
                    itemKey="providerSubscriptionDetails"
                    getFieldValue={getFieldValue}
                />
            </DetailsSection>
            <DetailsSection title={t('companies.details.sections.datesAndLimits')}>
                <CompanyFieldGrid fields={datesAndLimitsFields} getFieldValue={getFieldValue} />
            </DetailsSection>
            <DetailsSection title={t('companies.details.sections.billingConfiguration')}>
                <CompanyFieldGrid fields={billingConfigurationFields} getFieldValue={getFieldValue} />
            </DetailsSection>
            <CompanyDetailsFooter
                hasChanges={hasChanges}
                isUpdatingCompany={isUpdatingCompany}
                onClose={onClose}
                onSave={onSave}
            />
        </div>
    );
};

/**
 * Renders one subscription summary value.
 *
 * @param {object} props
 * @param {string} props.label
 * @param {string} props.value
 */
const SummaryItem = ({ label, value }) => (
    <div className={css(styles.summaryItem)}>
        <span className={css(styles.summaryLabel)}>{label}</span>
        <span className={css(styles.summaryValue)}>{value}</span>
    </div>
);

const styles = StyleSheet.create({
    subscriptionSummary: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: spacing.s1,
        padding: spacing.s3,
        backgroundColor: colors.backgroundLight,
        borderRadius: spacing.s1,
        '@media (max-width: 640px)': {
            gridTemplateColumns: 'minmax(0, 1fr)'
        }
    },
    summaryItem: {
        minWidth: 0
    },
    summaryLabel: {
        display: 'block',
        ...fonts.xsNormal,
        color: colors.blackOpacity,
        marginBottom: spacing.s0
    },
    summaryValue: {
        display: 'block',
        ...fonts.smBold,
        color: colors.black,
        overflowWrap: 'anywhere'
    },
    subscriptionActions: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: spacing.s2,
        marginTop: spacing.s4,
        paddingTop: spacing.s4,
        borderTop: `1px solid ${colors.greyLight}`
    }
});
